import { ApiRequestError, type AssignmentDetailsRequest, type PropertyPerson } from "@shyne/shared/api";
import type { uuid } from "@/interfaces";
import type { Contact } from "@/models/chat.model";
import { organization_type } from "@/models/organization.model";
import type { PropertyUserMetadata } from "@/models/properties.model";
import type { User } from "@/models/user.model";

export type AssignedPerson = Pick<User, "id" | "name" | "email" | "phone" | "image">;

export interface PropertyUserRecord {
  id: uuid;
  property: uuid;
  is_primary_contact: boolean;
  meta_data: PropertyUserMetadata;
  created_at: string | null;
  user: AssignedPerson;
}

export function toPropertyUser(person: PropertyPerson): PropertyUserRecord {
  const meta: PropertyUserMetadata = {};
  if (person.details?.role) meta.role = person.details.role;
  if (person.notes) meta.notes = person.notes;
  return {
    id: person.id,
    property: person.property,
    is_primary_contact: person.onSiteContact,
    meta_data: meta,
    created_at: person.details?.assignedAt ?? null,
    user: {
      id: person.details?.person ?? "",
      name: person.name,
      email: person.details?.email ?? "",
      phone: person.phone ?? "",
      image: person.details?.image ?? "",
    },
  };
}

export function toAssignmentDetails(meta: PropertyUserMetadata, onSiteContact: boolean): AssignmentDetailsRequest {
  return { role: meta.role ?? null, notes: meta.notes ?? null, onSiteContact };
}

export const onSiteContactAmong = (people: readonly PropertyPerson[]): uuid | null => people.find((person) => person.onSiteContact)?.details?.person ?? null;

export type CustomerOfProperty = { id: uuid; name: string };

export function contactsAmong(people: readonly PropertyPerson[], customer: CustomerOfProperty, excludeUserId: uuid): Contact[] {
  return people.flatMap((person) =>
    person.details && person.details.person !== excludeUserId
      ? [{ id: person.details.person, name: person.name, avatar: person.details.image, organizationId: customer.id, organizationName: customer.name, organizationType: organization_type.pmc }]
      : [],
  );
}

export function notYetAssigned<T extends { id: uuid }>(team: readonly T[], assigned: readonly PropertyUserRecord[]): T[] {
  const people = new Set(assigned.map((assignment) => assignment.user.id));
  return team.filter((member) => !people.has(member.id));
}

export const failureMessage = (error: unknown, fallback: string): string => (error instanceof ApiRequestError ? error.message : fallback);
